import React from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../services/api';
import ScopeBadge from '../components/ScopeBadge';
import MonthField from '../components/MonthField';
import useScopeFilters from '../hooks/useScopeFilters';
import { formatDuration, formatDateLabel, getInitials, todayString } from '../utils/format';
import {
    Box, Typography, Alert, Skeleton, Stack, Select, MenuItem, FormControl, InputLabel,
    Dialog, IconButton, CircularProgress,
} from '@mui/material';
import {
    ChevronRightRounded, VisibilityOutlined, CloseRounded, CalendarTodayRounded,
} from '@mui/icons-material';

const ALL = '';
const CURRENT_MONTH = todayString().slice(0, 7);

// Attendance board palette — taken from the "Attendance (TM)" design so the
// productive / unproductive / idle colors match it exactly.
const C = {
    panel: '#0F1B3D', panel2: '#13214A', panel3: '#0E1938', line: '#22356A', line2: '#1A2954',
    text: '#E6ECFF', muted: '#A9B6DD', soft: '#C7D2F2', faint: '#8B99C4',
    prod: '#2DD4BF', prodT: '#5EEAD4', unprod: '#FB7185', unprodT: '#FDA4AF',
    idle: '#F5B84B', idleT: '#F8CB7A', accent: '#3F6AE8', track: '#1E2F5E', avatar: '#8FB0FF',
};
const MONO = '"JetBrains Mono", ui-monospace, monospace';

// Grouped (teams → members) and flat (one team) column layouts
const COLS_GROUPED = '56px minmax(230px,2.2fr) minmax(140px,1.2fr) repeat(4,minmax(92px,1fr)) minmax(190px,1.7fr) 100px';
const COLS_FLAT = 'minmax(240px,2.2fr) minmax(150px,1.3fr) repeat(4,minmax(92px,1fr)) minmax(190px,1.7fr) 100px';

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const EMPTY = { worked: 0, productive: 0, unproductive: 0, idle: 0 };

function monthLabel(month) {
    const [y, m] = month.split('-').map(Number);
    return `${MONTHS_LONG[m - 1]} ${y}`;
}

const pct = (part, whole) => (whole > 0 ? `${Math.round((part * 100) / whole)}%` : '—');

// Shift times are synced as "21:00" — show them as "9:00 PM"
function to12h(t) {
    const m = /^(\d{1,2}):(\d{2})/.exec(t || '');
    if (!m) return t || '';
    const h = Number(m[1]);
    return `${h % 12 || 12}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
}

// Member's figures for the current mode: today's record or month-to-date
const recordFor = (m, isToday) => (isToday ? (m.today || EMPTY) : m.month);

function sumStats(members, isToday) {
    const s = { ...EMPTY, n: members.length, active: 0, late: 0, present: 0, possible: 0 };
    members.forEach((m) => {
        const r = recordFor(m, isToday);
        Object.keys(EMPTY).forEach((k) => { s[k] += r[k] || 0; });
        if (isToday) {
            if (m.today) { s.active++; if (m.today.late) s.late++; }
        } else {
            s.present += m.month.present;
            s.possible += m.month.present + m.month.absent;
            s.late += m.month.late;
        }
    });
    return s;
}

function SplitBar({ p, u, i, w, height = 8 }) {
    const width = (v) => (w > 0 ? `${((v * 100) / w).toFixed(1)}%` : '0%');
    return (
        <Box sx={{ flex: 1, height, borderRadius: `${height / 2 + 2}px`, bgcolor: C.track, overflow: 'hidden', display: 'flex' }}>
            <Box sx={{ width: width(p), bgcolor: C.prod }} />
            <Box sx={{ width: width(u), bgcolor: C.unprod }} />
            <Box sx={{ width: width(i), bgcolor: C.idle }} />
        </Box>
    );
}

function Productivity({ r, height }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <SplitBar p={r.productive} u={r.unproductive} i={r.idle} w={r.worked} height={height} />
            <Box component="span" sx={{ width: 44, textAlign: 'right', fontFamily: MONO, fontSize: 13, fontWeight: 700 }}>
                {pct(r.productive, r.worked)}
            </Box>
        </Box>
    );
}

function Num({ children, color, bold, align = 'right' }) {
    return (
        <Box component="span" sx={{ fontFamily: MONO, fontSize: 14, textAlign: align, color: color || C.text, fontWeight: bold ? 700 : 500 }}>
            {children}
        </Box>
    );
}

// Worked / Productive / Unproductive / Idle cells — "—" when nothing was worked
function DurationCells({ r, bold, blankWhenEmpty = true }) {
    const d = (v) => (blankWhenEmpty && !r.worked ? '—' : formatDuration(v));
    return (
        <>
            <Num bold={bold}>{d(r.worked)}</Num>
            <Num bold={bold} color={C.prodT}>{d(r.productive)}</Num>
            <Num bold={bold} color={C.unprodT}>{d(r.unproductive)}</Num>
            <Num bold={bold} color={C.idleT}>{d(r.idle)}</Num>
        </>
    );
}

function Row({ cols, sx, children }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: '12px', alignItems: 'center', px: '20px', py: '12px', ...sx }}>
            {children}
        </Box>
    );
}

function Avatar({ name, lead, size = 36 }) {
    return (
        <Box aria-hidden sx={{
            flex: 'none', width: size, height: size, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: Math.round(size * 0.36), color: '#0A1430', bgcolor: lead ? C.idle : C.avatar,
        }}>
            {getInitials(name)}
        </Box>
    );
}

function Badge({ children }) {
    return (
        <Box component="span" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', px: '7px', py: '2px', borderRadius: '6px', bgcolor: '#2A3F82', color: '#DCE5FF' }}>
            {children}
        </Box>
    );
}

function Chip({ children }) {
    return (
        <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: C.soft, px: '12px', py: '5px', borderRadius: '999px', bgcolor: C.panel2, border: `1px solid ${C.line}` }}>
            {children}
        </Box>
    );
}

// Check-in cell: today's time + late/on-time, or month average + present/late counts
function checkinCell(m, isToday, avgPrefix) {
    if (isToday) {
        if (m.today) {
            return m.today.late
                ? { main: m.today.checkin || '—', sub: `Late · ${m.today.offset} min`, color: C.idleT }
                : { main: m.today.checkin || '—', sub: 'On time', color: C.prodT };
        }
        return m.today_off
            ? { main: '—', sub: 'Off today', color: C.muted }
            : { main: '—', sub: 'Not checked in', color: C.unprodT };
    }
    const mo = m.month;
    return {
        main: mo.avg_checkin ? `${avgPrefix}${mo.avg_checkin}` : '—',
        sub: `${mo.present}/${mo.present + mo.absent} present · ${mo.late} late`,
        color: C.muted,
    };
}

function ViewButton({ name, onClick }) {
    return (
        <Box component="button" type="button" onClick={onClick} aria-label={`View month data for ${name}`} sx={{
            minHeight: 40, px: '14px', borderRadius: '10px', border: '1px solid #3A5BB0', bgcolor: '#1B3070', color: '#DCE5FF',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer',
            '&:hover': { bgcolor: '#22397F' }, '&:focus-visible': { outline: '2px solid #8FB0FF', outlineOffset: '2px' },
        }}>
            <VisibilityOutlined sx={{ fontSize: 16 }} />View
        </Box>
    );
}

function MemberRow({ m, cols, grouped, isToday, isMe, onView }) {
    const r = recordFor(m, isToday);
    const cell = checkinCell(m, isToday, grouped ? 'Avg ' : '');
    return (
        <Row cols={cols} sx={{
            borderTop: grouped ? `1px solid ${C.line2}` : 'none',
            borderBottom: grouped ? 'none' : `1px solid ${C.line2}`,
            bgcolor: isMe && !grouped ? C.panel2 : 'transparent',
        }}>
            {grouped && <span />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', pl: grouped ? '4px' : 0, minWidth: 0 }}>
                <Avatar name={m.name} lead={m.is_lead} />
                <Stack spacing="2px" sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 14, fontWeight: 700, flexWrap: 'wrap' }}>
                        {m.name}
                        {isMe && m.is_lead && <Badge>YOU · TEAM LEAD</Badge>}
                        {isMe && !m.is_lead && <Badge>YOU</Badge>}
                        {!isMe && m.is_lead && <Badge>TEAM LEAD</Badge>}
                    </Box>
                    <Typography sx={{ fontSize: 12, color: C.muted }}>
                        {[m.role, m.employee_id].filter(Boolean).join(' · ')}
                    </Typography>
                </Stack>
            </Box>
            <Stack spacing="3px">
                <Num bold align="left">{cell.main}</Num>
                <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: cell.color }}>{cell.sub}</Box>
            </Stack>
            <DurationCells r={r} />
            <Productivity r={r} />
            <ViewButton name={m.name} onClick={() => onView(m)} />
        </Row>
    );
}

function TeamGroup({ team, isToday, open, onToggle, viewerId, onView }) {
    const S = sumStats(team.members, isToday);
    const n = team.members.length;
    return (
        <Box sx={{ borderBottom: `1px solid ${C.track}` }}>
            <Row cols={COLS_GROUPED} sx={{ bgcolor: C.panel2, py: '14px' }}>
                <IconButton
                    onClick={onToggle}
                    aria-expanded={open}
                    aria-label={`${open ? 'Collapse' : 'Expand'} ${team.team_name}`}
                    sx={{ width: 44, height: 44, borderRadius: '10px', border: '1px solid #2E4688', bgcolor: '#18295A', color: '#DCE5FF', '&:hover': { bgcolor: '#1F3370' } }}
                >
                    <ChevronRightRounded sx={{ transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none' }} />
                </IconButton>
                <Stack spacing="3px" sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800 }} noWrap>{team.team_name}</Typography>
                    <Typography sx={{ fontSize: 12, color: C.muted }}>
                        {team.lead_name ? `Lead: ${team.lead_name} · ` : ''}{n} member{n === 1 ? '' : 's'}
                    </Typography>
                </Stack>
                <Stack spacing="3px">
                    <Box component="span" sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 700 }}>
                        {isToday ? `${S.active} / ${n} active` : `${S.present} / ${S.possible} days`}
                    </Box>
                    <Typography sx={{ fontSize: 12, color: C.muted }}>
                        {isToday ? `${S.late} late · ${n - S.active} not in` : `${S.late} lates this month`}
                    </Typography>
                </Stack>
                <DurationCells r={S} bold blankWhenEmpty={false} />
                <Productivity r={S} height={10} />
                <span />
            </Row>
            {open && (
                <Box sx={{ bgcolor: C.panel3 }}>
                    {team.members.map((m) => (
                        <MemberRow key={m.user_id} m={m} cols={COLS_GROUPED} grouped isToday={isToday}
                            isMe={m.user_id === viewerId} onView={(mem) => onView(mem, team)} />
                    ))}
                </Box>
            )}
        </Box>
    );
}

function FlatTeam({ team, isToday, viewerId, onView }) {
    const S = sumStats(team.members, isToday);
    // Viewer first, then the order the API returned (lead first, then by name)
    const members = [...team.members].sort((a, b) => (b.user_id === viewerId) - (a.user_id === viewerId));
    return (
        <>
            {members.map((m) => (
                <MemberRow key={m.user_id} m={m} cols={COLS_FLAT} isToday={isToday}
                    isMe={m.user_id === viewerId} onView={(mem) => onView(mem, team)} />
            ))}
            {members.length > 1 && (
                <Row cols={COLS_FLAT} sx={{ bgcolor: C.panel3 }}>
                    <Typography sx={{ fontWeight: 700 }}>Team total</Typography>
                    <Typography sx={{ fontSize: 12, color: C.muted }}>
                        {isToday ? `${S.active} of ${S.n} checked in` : `${S.present} / ${S.possible} days`}
                    </Typography>
                    <DurationCells r={S} bold blankWhenEmpty={false} />
                    <Productivity r={S} />
                    <span />
                </Row>
            )}
        </>
    );
}

const PILL = {
    'On Time': { bg: '#123F3B', color: C.prodT },
    Late: { bg: '#4A3814', color: C.idleT },
    Absent: { bg: '#4A1E33', color: C.unprodT },
    'Not checked in': { bg: '#4A1E33', color: C.unprodT },
    Off: { bg: '#2A3A66', color: C.soft },
};

function Tile({ label, value, color }) {
    return (
        <Box sx={{ bgcolor: C.panel2, border: `1px solid ${C.line}`, borderRadius: '12px', px: '14px', py: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>{label}</Box>
            <Box component="span" sx={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, color: color || C.text }}>{value}</Box>
        </Box>
    );
}

function MemberMonthDialog({ member, team, month, isMe, onClose }) {
    const [days, setDays] = React.useState(null);
    const [error, setError] = React.useState('');
    const mo = member.month;

    React.useEffect(() => {
        let cancelled = false;
        dashboardAPI.getAttendanceBoardDays(member.user_id, { month })
            .then((res) => {
                if (cancelled) return;
                if (res.data.error === '1') throw new Error(res.data.message || 'Failed to load days');
                setDays({ list: res.data.days || [], today: res.data.today });
            })
            .catch((err) => { if (!cancelled) setError(err.response?.data?.message || err.message || 'Failed to load days'); });
        return () => { cancelled = true; };
    }, [member.user_id, month]);

    const th = { px: '14px', py: '10px', fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', bgcolor: C.panel2, color: C.muted, textAlign: 'left', fontWeight: 700 };
    const td = { px: '14px', py: '9px', borderTop: `1px solid ${C.line2}` };
    const tdm = { ...td, fontFamily: MONO };

    return (
        <Dialog open onClose={onClose} maxWidth={false} PaperProps={{
            sx: { width: '100%', maxWidth: 1000, bgcolor: C.panel, backgroundImage: 'none', border: '1px solid #2E4688', borderRadius: '18px', color: C.text },
        }}>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: C.panel, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, px: '22px', pt: '20px', pb: '16px', borderBottom: `1px solid ${C.line}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <Avatar name={member.name} lead={member.is_lead} size={48} />
                    <Stack spacing="3px">
                        <Typography sx={{ fontSize: 20, fontWeight: 800 }}>{member.name}{isMe ? ' (you)' : ''}</Typography>
                        <Typography sx={{ fontSize: 13, color: C.muted }}>
                            {[member.role, member.employee_id, team.team_name, monthLabel(month)].filter(Boolean).join(' · ')}
                        </Typography>
                    </Stack>
                </Box>
                <IconButton onClick={onClose} aria-label="Close" sx={{ width: 44, height: 44, borderRadius: '10px', border: '1px solid #2E4688', bgcolor: '#18295A', color: '#DCE5FF' }}>
                    <CloseRounded />
                </IconButton>
            </Box>

            <Box sx={{ px: '22px', py: '18px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                    <Tile label="Present" value={`${mo.present} / ${mo.present + mo.absent}`} />
                    <Tile label="Late" value={mo.late} color={C.idleT} />
                    <Tile label="Absent" value={mo.absent} color={C.unprodT} />
                    <Tile label="Off" value={mo.off} color={C.soft} />
                    <Tile label="Avg check-in" value={mo.avg_checkin || '—'} />
                    <Tile label="Worked" value={formatDuration(mo.worked)} />
                    <Tile label="Productive" value={formatDuration(mo.productive)} color={C.prodT} />
                    <Tile label="Unproductive" value={formatDuration(mo.unproductive)} color={C.unprodT} />
                    <Tile label="Idle" value={formatDuration(mo.idle)} color={C.idleT} />
                    <Tile label="Productivity" value={pct(mo.productive, mo.worked)} />
                </Box>

                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.soft, fontWeight: 600, mb: 1 }}>
                        <span>Month time split</span>
                        <Box component="span" sx={{ fontFamily: MONO }}>{formatDuration(mo.worked)} worked</Box>
                    </Box>
                    <SplitBar p={mo.productive} u={mo.unproductive} i={mo.idle} w={mo.worked} height={14} />
                </Box>

                {error && <Alert severity="error">{error}</Alert>}
                <Box sx={{ overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: '12px' }}>
                    <Box component="table" sx={{ width: '100%', minWidth: 960, borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr>
                                <Box component="th" scope="col" sx={th}>Date</Box>
                                <Box component="th" scope="col" sx={th}>Status</Box>
                                <Box component="th" scope="col" sx={th}>Check-in</Box>
                                <Box component="th" scope="col" sx={{ ...th, textAlign: 'right' }}>Worked</Box>
                                <Box component="th" scope="col" sx={{ ...th, textAlign: 'right' }}>Productive</Box>
                                <Box component="th" scope="col" sx={{ ...th, textAlign: 'right' }}>Unproductive</Box>
                                <Box component="th" scope="col" sx={{ ...th, textAlign: 'right' }}>Idle</Box>
                                <Box component="th" scope="col" sx={{ ...th, minWidth: 190 }}>Productivity</Box>
                            </tr>
                        </thead>
                        <tbody>
                            {!days && !error && (
                                <tr><Box component="td" colSpan={8} sx={{ ...td, textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box></tr>
                            )}
                            {days && days.list.length === 0 && (
                                <tr><Box component="td" colSpan={8} sx={{ ...td, textAlign: 'center', py: 4, color: C.muted }}>No days in this month yet.</Box></tr>
                            )}
                            {days && days.list.map((d) => {
                                const has = d.checked_in;
                                const isToday = d.date === days.today;
                                const pill = PILL[d.status] || PILL.Off;
                                const rowSx = { ...(isToday ? { bgcolor: '#15244F' } : {}), ...(d.status === 'Off' ? { color: C.faint } : {}) };
                                const v = (x) => (has ? formatDuration(x) : '—');
                                return (
                                    <Box component="tr" key={d.date} sx={{ '& td': rowSx }}>
                                        <Box component="td" sx={tdm}>{formatDateLabel(d.date)}{isToday ? ' · today' : ''}</Box>
                                        <Box component="td" sx={td}>
                                            <Box component="span" sx={{ display: 'inline-block', px: '9px', py: '3px', borderRadius: '999px', fontSize: 12, fontWeight: 700, bgcolor: pill.bg, color: pill.color }}>
                                                {d.status === 'Late' ? `Late ${d.offset}m` : d.status}
                                            </Box>
                                        </Box>
                                        <Box component="td" sx={tdm}>{has ? (d.checkin || '—') : '—'}</Box>
                                        <Box component="td" sx={{ ...tdm, textAlign: 'right' }}>{v(d.worked)}</Box>
                                        <Box component="td" sx={{ ...tdm, textAlign: 'right', color: C.prodT }}>{v(d.productive)}</Box>
                                        <Box component="td" sx={{ ...tdm, textAlign: 'right', color: C.unprodT }}>{v(d.unproductive)}</Box>
                                        <Box component="td" sx={{ ...tdm, textAlign: 'right', color: C.idleT }}>{v(d.idle)}</Box>
                                        <Box component="td" sx={td}>{has ? <Productivity r={d} /> : <Productivity r={EMPTY} />}</Box>
                                    </Box>
                                );
                            })}
                        </tbody>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}

function SegButton({ active, onClick, children }) {
    return (
        <Box component="button" type="button" aria-pressed={active} onClick={onClick} sx={{
            minHeight: 40, px: '16px', borderRadius: '9px', border: 0, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            bgcolor: active ? C.accent : 'transparent', color: active ? '#fff' : C.soft,
            '&:focus-visible': { outline: '2px solid #8FB0FF', outlineOffset: '2px' },
        }}>
            {children}
        </Box>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const {
        level: scopeLevel, ready: scopeReady, units, teams: teamOptions,
        unitId, setUnitId, teamId, setTeamId, showUnitFilter, showTeamFilter,
    } = useScopeFilters('dashboard');

    const [mode, setMode]       = React.useState('today');
    const [month, setMonth]     = React.useState(CURRENT_MONTH);
    const [data, setData]       = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError]     = React.useState('');
    const [open, setOpen]       = React.useState({});
    const [viewing, setViewing] = React.useState(null); // { member, team }

    const isToday = mode === 'today';
    // Today always reads the current month; Whole month can browse back
    const fetchMonth = isToday ? CURRENT_MONTH : month;

    // Same guard as before: an admin (or a multi-unit 'units' user) must pick
    // a unit first; everyone else is already bounded by their own scope.
    const needsUnitPick = showUnitFilter && !unitId;
    React.useEffect(() => {
        if (!scopeReady || needsUnitPick) { setData(null); return; }
        let cancelled = false;
        setLoading(true);
        setError('');
        dashboardAPI.getAttendanceBoard({ month: fetchMonth, unit_id: unitId || undefined, team_id: teamId || undefined })
            .then((res) => {
                if (cancelled) return;
                if (res.data.error === '1') throw new Error(res.data.message || 'Failed to load dashboard');
                setData(res.data);
            })
            .catch((err) => { if (!cancelled) setError(err.response?.data?.message || err.message || 'Failed to load dashboard'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [scopeReady, needsUnitPick, unitId, teamId, fetchMonth]);

    const teams = React.useMemo(() => (data?.teams || []), [data]);
    // a single team is expanded straight away; several start collapsed
    React.useEffect(() => {
        setOpen(teams.length === 1 ? { [teams[0].team_id]: true } : {});
    }, [teams]);

    const viewerId = data?.viewer_id || user?.user_id;
    const grouped = scopeLevel === 'all' || scopeLevel === 'units' || teams.length > 1;
    const memberCount = teams.reduce((a, t) => a + t.members.length, 0);
    const today = data?.today || todayString();

    const title = grouped
        ? (data?.unit?.unit_name || units.find((u) => u.unit_id === unitId)?.unit_name || 'Attendance')
        : (teams[0] && teams[0].team_id !== 'none' ? teams[0].team_name : (user?.name || 'Attendance'));

    const shiftChip = React.useMemo(() => {
        const set = new Map();
        teams.forEach((t) => t.members.forEach((m) => {
            if (m.shift) set.set(`${m.shift.start_time}-${m.shift.end_time}`, m.shift);
        }));
        if (set.size === 0) return null;
        if (set.size > 1) return `${set.size} shifts`;
        const s = [...set.values()][0];
        return `Shift ${to12h(s.start_time)} – ${to12h(s.end_time)}`;
    }, [teams]);

    const tableNote = isToday
        ? 'Today'
        : (fetchMonth === CURRENT_MONTH ? `Month to date · ${monthLabel(fetchMonth)}` : monthLabel(fetchMonth));

    return (
        <Box sx={{ color: C.text }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.02em' }}>{needsUnitPick ? 'Attendance' : title}</Typography>
                        <ScopeBadge featureKey="dashboard" />
                    </Stack>
                    {!needsUnitPick && data && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: '10px' }}>
                            <Chip>Attendance log</Chip>
                            {grouped && <Chip>{teams.length} team{teams.length === 1 ? '' : 's'}</Chip>}
                            <Chip>{memberCount} member{memberCount === 1 ? '' : 's'}</Chip>
                            {shiftChip && <Chip>{shiftChip}</Chip>}
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minHeight: 40, px: 2, borderRadius: '12px', bgcolor: C.idle, color: '#0A1430', fontFamily: MONO, fontSize: 15, fontWeight: 700 }}>
                        <CalendarTodayRounded sx={{ fontSize: 16 }} />
                        {isToday ? formatDateLabel(today) : monthLabel(fetchMonth)}
                    </Box>
                    <Box role="group" aria-label="Period" sx={{ display: 'flex', gap: '4px', p: '4px', bgcolor: C.panel2, border: '1px solid #263A72', borderRadius: '12px' }}>
                        <SegButton active={isToday} onClick={() => setMode('today')}>Today</SegButton>
                        <SegButton active={!isToday} onClick={() => setMode('month')}>Whole month</SegButton>
                    </Box>
                </Box>
            </Box>

            {/* Filters */}
            {(showUnitFilter || showTeamFilter || !isToday) && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                    {showUnitFilter && (
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Unit</InputLabel>
                            <Select label="Unit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                                <MenuItem value={ALL}>Select a unit…</MenuItem>
                                {units.map((u) => <MenuItem key={u.unit_id} value={u.unit_id}>{u.unit_name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                    {showTeamFilter && (
                        <FormControl size="small" sx={{ minWidth: 180 }} disabled={needsUnitPick}>
                            <InputLabel>Team</InputLabel>
                            <Select label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                                {scopeLevel !== 'teams' && <MenuItem value={ALL}>All Teams</MenuItem>}
                                {teamOptions.map((t) => <MenuItem key={t.team_id} value={t.team_id}>{t.team_name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                    {!isToday && <MonthField value={month} onChange={setMonth} sx={{ minWidth: 160 }} />}
                </Stack>
            )}

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {needsUnitPick ? (
                <Box sx={{ p: 6, textAlign: 'center', bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: '18px' }}>
                    <Typography variant="h4" sx={{ mb: 1 }}>Select a Unit to get started</Typography>
                    <Typography sx={{ color: C.muted }}>
                        Dashboard data loads per unit — pick one above rather than pulling the whole company at once.
                    </Typography>
                </Box>
            ) : (
                <Box component="section" aria-label="Attendance" sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: '18px', pt: '20px', pb: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px', px: '20px', pb: 2 }}>
                        <Stack spacing="4px">
                            <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Attendance</Typography>
                            <Typography sx={{ fontSize: 13, color: C.muted }}>
                                {tableNote}{grouped ? ' · expand a team to see each member' : ''}
                            </Typography>
                        </Stack>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: 12, fontWeight: 600, color: C.soft }}>
                            {[['Productive', C.prod], ['Unproductive', C.unprod], ['Idle', C.idle]].map(([l, c]) => (
                                <Box key={l} component="span" sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Box component="span" sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: c }} />{l}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: grouped ? 1140 : 1080 }}>
                            <Row cols={grouped ? COLS_GROUPED : COLS_FLAT} sx={{
                                py: '10px', fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: C.muted,
                                borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}`,
                            }}>
                                {grouped && <span />}
                                <span>{grouped ? 'Team / Employee' : 'Employee'}</span>
                                <span>{grouped ? (isToday ? 'Active today' : 'Present days') : (isToday ? 'Check-in' : 'Avg check-in')}</span>
                                <Box component="span" sx={{ textAlign: 'right' }}>Worked</Box>
                                <Box component="span" sx={{ textAlign: 'right' }}>Productive</Box>
                                <Box component="span" sx={{ textAlign: 'right' }}>Unproductive</Box>
                                <Box component="span" sx={{ textAlign: 'right' }}>Idle</Box>
                                <span>Productivity</span>
                                <span />
                            </Row>

                            {loading && !data ? (
                                [0, 1, 2].map((i) => (
                                    <Box key={i} sx={{ px: '20px', py: '10px' }}>
                                        <Skeleton variant="rectangular" height={48} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '10px' }} />
                                    </Box>
                                ))
                            ) : teams.length === 0 ? (
                                <Typography sx={{ textAlign: 'center', color: C.muted, py: 6 }}>
                                    {data ? 'No employees in your access scope.' : ''}
                                </Typography>
                            ) : grouped ? (
                                teams.map((t) => (
                                    <TeamGroup key={t.team_id} team={t} isToday={isToday} open={!!open[t.team_id]}
                                        onToggle={() => setOpen((o) => ({ ...o, [t.team_id]: !o[t.team_id] }))}
                                        viewerId={viewerId} onView={(member, team) => setViewing({ member, team })} />
                                ))
                            ) : (
                                <FlatTeam team={teams[0]} isToday={isToday} viewerId={viewerId}
                                    onView={(member, team) => setViewing({ member, team })} />
                            )}
                        </Box>
                    </Box>
                    <Typography sx={{ m: 0, px: '20px', pt: '14px', pb: 1, fontSize: 12, color: C.faint }}>
                        Productivity = productive time ÷ worked time. Worked = active + idle; unproductive = active time not rated productive.
                        Late = check-in after shift start + the shift's late margin.
                    </Typography>
                </Box>
            )}

            {viewing && (
                <MemberMonthDialog
                    member={viewing.member}
                    team={viewing.team}
                    month={data?.month || fetchMonth}
                    isMe={viewing.member.user_id === viewerId}
                    onClose={() => setViewing(null)}
                />
            )}
        </Box>
    );
}
